import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

// BrowserRouter сам прокрутку не восстанавливает, а браузерное восстановление
// срабатывает раньше, чем React дорисует страницу, — и прокрутка падает в 0.
// Поэтому ведём её сами: позиция на каждую запись истории (location.key),
// «назад/вперёд» возвращает её, переход на другую страницу — наверх.

const STORAGE_KEY = 'resstro-manager-scroll';
const MAX_ENTRIES = 50;

const positions = new Map<string, number>(readStored());

function readStored(): Array<[string, number]> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return Array.isArray(parsed) ? (parsed as Array<[string, number]>) : [];
  } catch {
    return [];
  }
}

function persist() {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...positions].slice(-MAX_ENTRIES)));
  } catch {
    /* приватный режим / квота — живём без перезагрузочного восстановления */
  }
}

/** Документ подняли перезагрузкой или «назад» с чужого сайта — прокрутку стоит вернуть. */
function documentWasRestored(): boolean {
  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  return nav?.type === 'reload' || nav?.type === 'back_forward';
}

function remember(key: string, y: number) {
  // delete + set — свежие записи в конце, при сохранении обрезаем старые.
  positions.delete(key);
  positions.set(key, y);
}

/**
 * Прокрутить к y. Если страница ещё короче (карточка ждёт ответа сервера) —
 * дожимаем по мере появления контента, пока пользователь сам не начал листать.
 */
function restoreTo(y: number): () => void {
  const reachable = () => document.documentElement.scrollHeight - window.innerHeight >= y - 1;
  window.scrollTo(0, y);
  if (reachable()) return () => {};

  let frame = 0;
  const attempt = () => {
    frame = 0;
    window.scrollTo(0, y);
    if (reachable()) stop();
  };
  const observer = new MutationObserver(() => {
    if (!frame) frame = window.requestAnimationFrame(attempt);
  });
  const timer = window.setTimeout(() => stop(), 4000);
  function stop() {
    observer.disconnect();
    if (frame) window.cancelAnimationFrame(frame);
    window.clearTimeout(timer);
    window.removeEventListener('wheel', stop);
    window.removeEventListener('touchstart', stop);
    window.removeEventListener('keydown', stop);
  }
  observer.observe(document.body, { childList: true, subtree: true });
  window.addEventListener('wheel', stop, { passive: true });
  window.addEventListener('touchstart', stop, { passive: true });
  window.addEventListener('keydown', stop);
  return stop;
}

export default function ScrollManager() {
  const { key, pathname } = useLocation();
  const navigationType = useNavigationType();
  const keyRef = useRef(key);
  const pathRef = useRef(pathname);
  const prevKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!('scrollRestoration' in window.history)) return;
    const prev = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = prev;
    };
  }, []);

  useEffect(() => {
    const onScroll = () => remember(keyRef.current, window.scrollY);
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') persist();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', persist);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', persist);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Layout-эффект: DOM новой страницы уже на месте, а кадр ещё не нарисован —
  // прокрутка встаёт без мигания.
  useLayoutEffect(() => {
    const pathChanged = pathRef.current !== pathname;
    // Первый запуск (и его повтор в StrictMode) — это загрузка документа, а не «назад».
    const initial = prevKeyRef.current === null || prevKeyRef.current === key;
    keyRef.current = key;
    pathRef.current = pathname;
    prevKeyRef.current = key;

    if (navigationType === 'POP') {
      // Свежий заход по ссылке начинается сверху: у его первой записи ключ
      // 'default', и позиция могла остаться от прошлого визита в этой вкладке.
      if (initial && !documentWasRestored()) return undefined;
      const y = positions.get(key);
      return y == null ? undefined : restoreTo(y);
    }
    // Смена фильтров в адресе (тот же путь) не должна бросать наверх.
    if (pathChanged) window.scrollTo(0, 0);
    return undefined;
  }, [key, pathname, navigationType]);

  return null;
}
